import i18n from '../i18n'
import { getAddressList } from '@src/api/memberAddress'
import { AddressState } from '@src/const/address'
/**
 * Geolocation options
 *
 * @type {{enableHighAcuracy: boolean, maximumAge: number, timeout: number}}
 */
const options = {
  enableHighAccuracy: true,
  timeout: 50000,
  maximumAge: 0
}

/**
 * Geolocation error codes
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError/code
 */
const errorMessages = {
  1: i18n.t('errors.please_check_geolocation_permission'),
  2: i18n.t('errors.unavailable'),
  3: i18n.t('errors.timeout'),
  4: i18n.t('errors.browser_version_unsupported_geolocation') // custom
}

/**
 * 過濾地址組件類型
 *
 * @see https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
 */
const filterAddressComponentTypes = [
  'administrative_area_level_1',
  'administrative_area_level_3',
  'route',
  'street_number'
]

/**
 * Geolocation methods
 */
const METHOD_GET_CURRENT_POSITION = 'getCurrentPosition'
const METHOD_WATCH_POSITION = 'watchPosition'

// eslint-disable-next-line no-unused-vars
let watchId

/**
 * Get position
 *
 * call watchPosition() when watch is true
 *
 * @param successCallback
 * @param errorCallback
 * @param watch
 * @returns {boolean}
 */
export function getPosition(successCallback, errorCallback, watch) {
  if (!supportGeolocation()) {
    errorCallback({ code: 4, msg: errorMessages[4] })
    return false
  }

  const func = watch ? METHOD_WATCH_POSITION : METHOD_GET_CURRENT_POSITION

  watchId = navigator.geolocation[func]((res) => {
    successCallback(res)
  }, (error) => {
    errorCallback({ code: error.code, msg: errorMessages[error.code] })
  }, options)
}

export function clearWatch() {
  if (watchId === undefined) {
    return
  }

  navigator.geolocation.clearWatch(watchId)
}

/**
 * Determine browser version is support geolocation
 *
 * @returns {boolean}
 */
export function supportGeolocation() {
  if (navigator.geolocation) {
    return true
  }
  return false
}


/**
 * 編碼轉換
 *
 * @param Object options
 * @returns Promise
 */
export async function geocode(options) {
  const maps = (!!options.vueInstance) ? options.vueInstance.$google.maps: this.$google.maps

  if (options.hasOwnProperty("vueInstance")) {
    delete options.vueInstance
  }

  const geocoder = new maps.Geocoder()
  return new Promise((resolve, reject) => {
    try {
      geocoder.geocode(options, (result, status) => {
        if (status !== maps.GeocoderStatus.OK) {
          throw new Error(`Geocoder Request Failed: ${status}`)
        }
        result = result.sort(function (a, b) {
          return b.address_components.length - a.address_components.length
        })
        resolve(result.shift())
      })
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 格式化地址
 *
 * @param Array addressComponents 
 * @returns String
 */
export function transformAddress(addressComponents) {
  return addressComponents.reverse().reduce((previousValue, currentValue, currentIndex) => {
    for (let i = currentValue['types'].length - 1; i >= 0; i--) {
      const type = currentValue['types'][i]
      const longName = currentValue['long_name']
      if (filterAddressComponentTypes.indexOf(type) > -1) {
        if (type === 'street_number' && longName.indexOf('號') === -1) {
          previousValue += longName + '號'
        } else {
          previousValue += longName
        }
      }
    }
    return previousValue
  }, "")
}

/**
 * 
 * Get straight line distance
 * 
 * @param {*} p1 
 * @param {*} p2 
 * @returns 
 */
export function getStraightLineDistance(p1, p2) {
  const radLat1 = p1.lat * Math.PI / 180.0
  const radLat2 = p2.lat * Math.PI / 180.0
  const a = radLat1 - radLat2
  const b = (p1.lng * Math.PI / 180.0) - (p2.lng * Math.PI / 180.0)
  let s = 2 * Math.asin(Math.sqrt(Math.pow(Math.sin(a / 2), 2) + Math.cos(radLat1) * Math.cos(radLat2) * Math.pow(Math.sin(b / 2), 2)))
  s = s * 6378.137
  return Math.round(s * 1000, 2)
}

/**
 * 檢測使用者目前位置與預設地址判斷應返回哪一個
 * 
 * @param vueInstance
 * 
 * @returns Promise
 */
export async function checkAddressPosition(vueInstance) {
  if (sessionStorage.getItem('checkAddress') === 'agree') {
    return false
  }

  let defaultPosition = vueInstance.$store.state.member.point;

  let currentPosition = await new Promise((resolve, reject) => {
    getPosition(function (res) {
      resolve({ lat: res.coords.latitude, lng: res.coords.longitude })
    }, (error) => {
      reject(error)
    })
  })

  const distance = getStraightLineDistance(currentPosition, defaultPosition)

  if (Math.round(distance) > process.env.VUE_APP_DETECT_BOTH_ADDRESSES_TOO_FAR_MEMTERS) {
      return true
  }
  return false
}